import { useCallback, useState } from 'react';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { useModal } from '@trinity/chat-components/components/Modal/hooks/index.js';
import type { ModalProps } from '@trinity/chat-components/components/Modal/Modal.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { Select } from '../presentation/Select';
import styles from './style/Modal.Export.module.css';
import { useDownloadProjectInsightsReport } from '@/api/hooks/project.insights.export';

type Props = IProjectId & Pick<ModalProps, 'onClose' | 'open'>;

export const ExportProjectInsightsModal = ({ onClose, open, projectId }: Props) => {
  const [format, setFormat] = useState<'docx' | 'pptx'>('pptx');

  const { download, isFetching } = useDownloadProjectInsightsReport({
    format,
    projectId,
  });

  const handleClickExport = useCallback(() => {
    download({
      extension: format,
      name: `Project Insights Summary`,
      title: `Generating Report`,
    });
    onClose();
  }, [
    download,
    format,
    onClose,
  ]);

  return (
    <Modal
      classes={{ root: styles.modal }}
      disableOverlayClick={isFetching}
      onClose={onClose}
      open={open}>
      <Header>Export Insights Summary</Header>

      <div className={styles.format}>

        <div className={styles.field}>
          <div className={styles.label}>File format:</div>

          <Select<Record<'pptx' | 'docx', string>>
            onChange={x => setFormat(x.target.value)}
            options={[
              { id: 'pptx', name: 'pptx' },
              { id: 'docx', name: 'docx' },
            ]}
            value={format} />
        </div>
      </div>

      <ButtonSet className={styles.footer}>
        <ButtonActivityIndicator
          className={styles.action}
          color="primary"
          loading={isFetching}
          onClick={handleClickExport}>
          Download
        </ButtonActivityIndicator>
      </ButtonSet>
    </Modal>
  );
};

ExportProjectInsightsModal.displayName = 'Insights.ExportModal';

export const useExportProjectInsightsModal = () => useModal(ExportProjectInsightsModal);